import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
    bulkUpdateFeatureFlagStatus,
    createFeatureFlag,
    createFeatureFlagGroup,
    deleteFeatureFlag,
    deleteFeatureFlagGroup,
    getFeatureFlagById,
    getFeatureFlagGroupById,
    getFeatureFlagGroups,
    getFeatureFlags,
    getTargetableFields,
    previewFeatureFlag,
    previewFeatureFlagGroup,
    toggleFeatureFlag,
    updateFeatureFlag,
    updateFeatureFlagGroup,
} from '@/services/feature-flags.service'
import type {
    CreateFeatureFlagGroupRequest,
    CreateFeatureFlagRequest,
    DeleteResponse,
    FeatureFlagDetailResponse,
    FeatureFlagFilters,
    FeatureFlagGroupDetailResponse,
    FeatureFlagGroupFilters,
    FeatureFlagGroupListResponse,
    FeatureFlagGroupMutationResponse,
    FeatureFlagListResponse,
    FeatureFlagMutationResponse,
    FeatureFlagPreviewResponse,
    FeatureFlagStatus,
    GroupPreviewResponse,
    PreviewFeatureFlagRequest,
    TargetableFieldsResponse,
    UpdateFeatureFlagGroupRequest,
    UpdateFeatureFlagRequest,
} from '@/types/feature-flag'

const FLAGS_KEY = 'feature-flags'
const GROUPS_KEY = 'feature-flag-groups'

/**
 * Feature flags list
 */
export const useFeatureFlags = (filters?: FeatureFlagFilters) => {
    return useQuery<FeatureFlagListResponse>({
        queryKey: [FLAGS_KEY, filters],
        queryFn: () => getFeatureFlags(filters),
        staleTime: 1000 * 30,
    })
}

/**
 * Single feature flag
 */
export const useFeatureFlag = (id?: string) => {
    return useQuery<FeatureFlagDetailResponse>({
        queryKey: ['feature-flag', id],
        queryFn: () => getFeatureFlagById(id as string),
        enabled: !!id,
        staleTime: 1000 * 60,
    })
}

/**
 * Fields and operators available to the rule builder.
 * Rarely changes — it only moves when the API ships a new targetable field.
 */
export const useTargetableFields = () => {
    return useQuery<TargetableFieldsResponse>({
        queryKey: ['feature-flag-fields'],
        queryFn: getTargetableFields,
        staleTime: 1000 * 60 * 60,
    })
}

/**
 * Create feature flag
 */
export const useCreateFeatureFlag = () => {
    const queryClient = useQueryClient()

    return useMutation<
        FeatureFlagMutationResponse,
        unknown,
        CreateFeatureFlagRequest
    >({
        mutationFn: data => createFeatureFlag(data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: [FLAGS_KEY] })
        },
    })
}

/**
 * Update feature flag
 */
export const useUpdateFeatureFlag = () => {
    const queryClient = useQueryClient()

    return useMutation<
        FeatureFlagMutationResponse,
        unknown,
        { id: string; data: UpdateFeatureFlagRequest }
    >({
        mutationFn: ({ id, data }) => updateFeatureFlag(id, data),
        onSuccess: (_data, variables) => {
            queryClient.invalidateQueries({ queryKey: [FLAGS_KEY] })
            queryClient.invalidateQueries({
                queryKey: ['feature-flag', variables.id],
            })
        },
    })
}

/**
 * Toggle a flag on or off — the kill switch used from the table row
 */
export const useToggleFeatureFlag = () => {
    const queryClient = useQueryClient()

    return useMutation<
        FeatureFlagMutationResponse,
        unknown,
        { id: string; enabled: boolean }
    >({
        mutationFn: ({ id, enabled }) => toggleFeatureFlag(id, enabled),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: [FLAGS_KEY] })
        },
    })
}

/**
 * Bulk status update
 */
export const useBulkUpdateFeatureFlagStatus = () => {
    const queryClient = useQueryClient()

    return useMutation<
        DeleteResponse,
        unknown,
        { ids: string[]; status: FeatureFlagStatus }
    >({
        mutationFn: ({ ids, status }) =>
            bulkUpdateFeatureFlagStatus(ids, status),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: [FLAGS_KEY] })
        },
    })
}

/**
 * Evaluate a flag as a given user/client. Read-only, so it's a mutation only
 * because it takes a body — it never changes server state.
 */
export const usePreviewFeatureFlag = () => {
    return useMutation<
        FeatureFlagPreviewResponse,
        unknown,
        { id: string; data: PreviewFeatureFlagRequest }
    >({
        mutationFn: ({ id, data }) => previewFeatureFlag(id, data),
    })
}

/**
 * Delete feature flag
 */
export const useDeleteFeatureFlag = () => {
    const queryClient = useQueryClient()

    return useMutation<DeleteResponse, unknown, string>({
        mutationFn: id => deleteFeatureFlag(id),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: [FLAGS_KEY] })
        },
    })
}

/* ================= Groups ================= */

/**
 * Feature flag groups list
 */
export const useFeatureFlagGroups = (filters?: FeatureFlagGroupFilters) => {
    return useQuery<FeatureFlagGroupListResponse>({
        queryKey: [GROUPS_KEY, filters],
        queryFn: () => getFeatureFlagGroups(filters),
        staleTime: 1000 * 30,
    })
}

/**
 * Single group
 */
export const useFeatureFlagGroup = (id?: string) => {
    return useQuery<FeatureFlagGroupDetailResponse>({
        queryKey: ['feature-flag-group', id],
        queryFn: () => getFeatureFlagGroupById(id as string),
        enabled: !!id,
        staleTime: 1000 * 60,
    })
}

/**
 * Create group
 */
export const useCreateFeatureFlagGroup = () => {
    const queryClient = useQueryClient()

    return useMutation<
        FeatureFlagGroupMutationResponse,
        unknown,
        CreateFeatureFlagGroupRequest
    >({
        mutationFn: data => createFeatureFlagGroup(data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: [GROUPS_KEY] })
        },
    })
}

/**
 * Update group
 */
export const useUpdateFeatureFlagGroup = () => {
    const queryClient = useQueryClient()

    return useMutation<
        FeatureFlagGroupMutationResponse,
        unknown,
        { id: string; data: UpdateFeatureFlagGroupRequest }
    >({
        mutationFn: ({ id, data }) => updateFeatureFlagGroup(id, data),
        onSuccess: (_data, variables) => {
            queryClient.invalidateQueries({ queryKey: [GROUPS_KEY] })
            queryClient.invalidateQueries({
                queryKey: ['feature-flag-group', variables.id],
            })
        },
    })
}

/**
 * Count the users a group covers
 */
export const usePreviewFeatureFlagGroup = () => {
    const queryClient = useQueryClient()

    return useMutation<GroupPreviewResponse, unknown, string>({
        mutationFn: id => previewFeatureFlagGroup(id),
        onSuccess: () => {
            // The preview writes back cachedCount/lastEvaluatedAt.
            queryClient.invalidateQueries({ queryKey: [GROUPS_KEY] })
        },
    })
}

/**
 * Delete group
 */
export const useDeleteFeatureFlagGroup = () => {
    const queryClient = useQueryClient()

    return useMutation<DeleteResponse, unknown, string>({
        mutationFn: id => deleteFeatureFlagGroup(id),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: [GROUPS_KEY] })
        },
    })
}
